/**
 * The theme switch, as one button rather than a labelled select.
 *
 * A select cost a label, a control and roughly a hundred and forty pixels of
 * the top bar to choose between two values. A toggle naming the theme it moves
 * to says the same thing in one control.
 *
 * @module components/shared/ThemeToggle
 */

import React, { useCallback, useState } from "react";

import { IconButton } from "@/components/ui";

import { applyTheme, currentTheme } from "./theme";
import type { ThemeName } from "./workspace-types";

export interface ThemeToggleProps {
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <ThemeToggle data-testid="global-theme-toggle" />
 * ```
 */
export function ThemeToggle({
  "data-testid": testId = "global-theme-toggle",
}: ThemeToggleProps): React.ReactElement {
  const [theme, setTheme] = useState<ThemeName>(currentTheme);

  const toggle = useCallback(() => {
    const next: ThemeName = theme === "dark" ? "light" : "dark";
    applyTheme(next);
    setTheme(next);
  }, [theme]);

  return (
    <IconButton
      // Names the destination, not the current state: "Dark theme" on a button
      // is ambiguous about whether it describes now or next.
      aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
      icon={theme === "dark" ? "sun" : "moon"}
      size="sm"
      onClick={toggle}
      data-testid={testId}
    />
  );
}
